<template>
	<div ref="tableWrapper" class="p-p-2 child-content">
		<DataTable
			:value="translationRows"
			:loading="loadingTranslations"
			class="p-datatable-sm translation-table"
			:paginator="true"
			v-model:filters="filters"
			filterDisplay="row"
			paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
			:rowsPerPageOptions="[25,50,100]"
			:first="firstRow" :rows="rowsPerPage" :rowClass="rowClass"
			columnResizeMode="fit"
			:currentPageReportTemplate="'{first} ' + $t('Squeeze.General.To') + ' {last} ' + $t('Squeeze.General.Of') + ' {totalRecords} '"
			:scrollable="true"
			scrollHeight="flex"
			:autoLayout="true"
			@row-dblclick="openEditDialog($event.data)" @page="onPage"
		>
			<template #loading>
				{{ $t('Squeeze.General.Loading') }}
			</template>
			<template #empty>
				{{ $t('Squeeze.General.NoEntriesFound', { entryName: $t('Squeeze.System.Translations') }) }}
			</template>
			<Column
				field="translationKey"
				filterField="translationKey"
				:header="$t('Squeeze.System.TranslationKey')"
				:showFilterMenu="false"
			>
				<template #filter="{filterModel, filterCallback}">
					<InputText
						v-model="filterModel.value"
						type="text"
						class="p-column-filter filter-field"
						style="min-width: 100%;"
						@input="filterCallback()"
					/>
				</template>
			</Column>
			<Column
				v-for="languageColumn in activeLanguageOptions"
				:key="languageColumn.countryCode"
				:field="'translations.' + languageColumn.countryCode"
				:filterField="'translations.' + languageColumn.countryCode"
				:header="languageColumn.translatedDescription"
				:showFilterMenu="false"
				style="min-width: 14rem;"
			>
				<template #body="slotProps">
					<div class="column-cell">
						{{ getTranslationValue(slotProps.data, languageColumn.countryCode) }}
					</div>
				</template>
				<template #filter="{filterModel, filterCallback}">
					<InputText
						v-model="filterModel.value"
						type="text"
						class="p-column-filter filter-field"
						style="min-width: 100%;"
						@input="filterCallback()"
					/>
				</template>
			</Column>
			<Column style="min-width: 5.6rem" frozen alignFrozen="right">
				<template #body="slotProps">
					<Button
						v-if="slotProps.data.hasStoredTranslations"
						v-tooltip.bottom="$t('Squeeze.Locators.Edit')"
						icon="mdi mdi-pencil-outline"
						class="p-button-lg p-button-rounded p-button-success p-button-text"
						@click.stop="openEditDialog(slotProps.data)"
					/>
					<Button
						v-if="slotProps.data.hasStoredTranslations"
						v-tooltip.bottom="$t('Squeeze.Validation.Buttons.Delete')"
						icon="mdi mdi-delete-outline"
						class="p-button-lg p-button-rounded p-button-danger p-button-text"
						@click.stop="openDeleteDialog(slotProps.data)"
					/>
					<Button
						v-if="!slotProps.data.hasStoredTranslations"
						v-tooltip.bottom="$t('Squeeze.General.NewEntry')"
						icon="mdi mdi-plus"
						class="p-button-lg p-button-rounded p-button-success p-button-text"
						@click.stop="openCreateDialog(slotProps.data)"
					/>
				</template>
			</Column>
			<template #paginatorstart>
				<Button
					v-if="!disabledNewEntryButton"
					:label="$t('Squeeze.General.NewEntry')"
					type="button"
					icon="mdi mdi-plus"
					class="p-button"
					:disabled="availableTranslationKeys.length === 0"
					@click="openCreateDialog()"
				/>
			</template>
			<template #paginatorend></template>
		</DataTable>
	</div>

	<DialogDelete
		:showDialog="deleteDialog"
		@onClose="deleteDialog = false"
		@onConfirm="deleteTranslation"
	/>

	<EntryDialog
		:show="showEditDialog"
		@onClose="closeEditDialog"
		@onConfirm="saveTranslation"
		:loading="loadingDialog"
		:width="'80rem'"
		:headerText="dialogHeaderText"
		:showKeepDialogOpen="false"
	>
		<template #content>
			<BlockUI :blocked="loadingDialog">
				<div class="p-fluid p-formgrid p-grid p-input-filled">
					<div class="p-field p-col-12">
						<label for="translationKey">{{ $t('Squeeze.System.TranslationKey') }} *</label>
						<InputText
							v-if="isEditMode"
							id="translationKey"
							v-model="translationEntry.translationKey"
							disabled
						/>
						<Dropdown
							v-else
							id="translationKey"
							v-model="translationEntry.translationKey"
							:options="availableTranslationKeys"
							:filter="true"
							:autoFilterFocus="true"
							optionValue="translationKey"
							optionLabel="translationKey"
							:class="{'p-invalid': isTranslationKeyInvalid && showValidationMessage}"
						/>
						<small v-if="isTranslationKeyInvalid && showValidationMessage" class="p-error">
							{{ $t('Forms.Val.MissingRequired', { field: $t('Squeeze.System.TranslationKey') }) }}
						</small>
					</div>
					<div
						v-for="language in activeLanguageOptions"
						:key="language.countryCode"
						class="p-field p-col-12 p-md-6"
					>
						<label :for="'translation-' + language.countryCode">{{ language.translatedDescription }}</label>
						<InputText
							:id="'translation-' + language.countryCode"
							v-model="translationEntry.translations[language.countryCode]"
						/>
					</div>
				</div>
			</BlockUI>
		</template>
	</EntryDialog>
</template>

<script lang="ts">
import {computed, defineComponent, onMounted, PropType, reactive, ref, watch} from "vue";
import {useI18n} from "vue-i18n";
import {useToast} from "primevue/usetoast";
import BlockUI from "primevue/blockui";
import Column from "primevue/column";
import DataTable from "primevue/datatable";
import Dropdown from "primevue/dropdown";
import InputText from "primevue/inputtext";
import Button from "primevue/button";
import {FilterMatchMode} from "primevue/api";
import {TranslationCountry, TranslationSet} from "@dex/squeeze-client-ts";
import DialogDelete from "@/components/DialogDelete.vue";
import EntryDialog from "@/components/EntryDialog.vue";
import {ClientManager} from "@/singletons/ClientManager";
import {ToastManager} from "@/util/ToastManager";
import useSqueezeStore from "@/apps/squeeze/store";
import {useTableRowFocus} from "@/composables/useTableRowFocus";

interface TranslationRow {
	id?: number | null;
	translationKey?: string;
	lang?: string;
	translation?: string;
}

interface TranslationText {
	lang: string;
	translation: string | null;
}

interface TranslationTableRow {
	translationKey: string;
	translations: Record<string, string>;
	hasStoredTranslations: boolean;
}

interface TranslationEntry {
	translationKey: string;
	translations: Record<string, string>;
}

type ActiveTranslationCountry = TranslationCountry & {countryCode: string};
type TranslationFilters = Record<string, {value: string | null; matchMode: string}>;

export default defineComponent({
	name: "TranslationListView",
	components: {
		BlockUI,
		Button,
		Column,
		DataTable,
		DialogDelete,
		Dropdown,
		EntryDialog,
		InputText,
	},
	props: {
		activeLanguages: {
			type: Array as PropType<TranslationCountry[]>,
			default: () => [],
		},
		fixedFilter: {
			type: String,
			default: '',
		},
		/**
		 * Optional allow-list of translation keys. When set, only rows whose key equals one of these keys
		 * or starts with "<key>." are shown. When null, all keys from the "fixedFilter" are shown.
		 * This parameter is only to filter specific keys in the frontend,
		 * example special case masterdata (columns without activated translation are removed).
		 */
		allowedKeys: {
			type: Array as PropType<string[] | null>,
			default: null,
		},
		disabledNewEntryButton: {
			type: Boolean,
			default: false,
		},
	},
	emits: [
		"keysLoaded",
	],
	setup(props, {emit}) {
		const {t} = useI18n();
		const toast = useToast();
		const store = useSqueezeStore();

		/** Show Loading on load translation data */
		const loadingTranslations = ref<boolean>(false);

		/** Show loading state in create/edit dialog */
		const loadingDialog = ref<boolean>(false);

		/** All translations returned by the API including generated missing entries */
		const translations = ref<TranslationRow[]>([]);

		/** Should the create/edit dialog be shown? */
		const showEditDialog = ref<boolean>(false);

		/** Should validation messages be shown in create/edit dialog? */
		const showValidationMessage = ref<boolean>(false);

		/** Is the create/edit dialog currently editing an existing key? */
		const isEditMode = ref<boolean>(false);

		/** Should the delete confirmation dialog be shown? */
		const deleteDialog = ref<boolean>(false);

		/** Translation row selected for deletion */
		const selectedTranslation = ref<TranslationTableRow | null>(null);

		/** Current create/edit dialog values */
		const translationEntry = reactive<TranslationEntry>({
			translationKey: '',
			translations: {},
		});

		/** Active language options with a guaranteed language code for table columns and dialog inputs */
		const activeLanguageOptions = computed<ActiveTranslationCountry[]>(() => {
			const languages = props.activeLanguages.length > 0 ? props.activeLanguages : store.state.translationCountries;
			return languages
				.filter((language: TranslationCountry): language is ActiveTranslationCountry => !!language.active && !!language.countryCode)
				.sort((a: ActiveTranslationCountry, b: ActiveTranslationCountry) => a.countryCode.localeCompare(b.countryCode));
		});

		/** PrimeVue DataTable filters used for local column filtering */
		const filters = ref<TranslationFilters>({});

		/** Initializes local DataTable filters for the key column and all active language columns */
		const initFilters = () => {
			const tableFilters: TranslationFilters = {
				translationKey: {value: null, matchMode: FilterMatchMode.CONTAINS},
			};

			activeLanguageOptions.value.forEach((language: ActiveTranslationCountry) => {
				tableFilters["translations." + language.countryCode] = {value: null, matchMode: FilterMatchMode.CONTAINS};
			});

			filters.value = tableFilters;
		};

		/** Header text for create/edit dialog */
		const dialogHeaderText = computed<string>(() => {
			return isEditMode.value
				? t('Squeeze.General.ChangeEntry', {entryName: t('Squeeze.System.Translations')})
				: t('Squeeze.General.CreateEntry', {entryName: t('Squeeze.System.Translations')});
		});

		/** Checks whether the key field is missing in the create dialog */
		const isTranslationKeyInvalid = computed<boolean>(() => translationEntry.translationKey.trim() === '');

		/**
		 * Checks whether a translation key is allowed by the optional allowedKeys allow-list.
		 * A key is allowed when it equals an allowed key or is a sub-key ("<allowedKey>.…").
		 * When no allow-list is set, every key is allowed.
		 */
		const isKeyAllowed = (translationKey: string): boolean => {
			if (!props.allowedKeys) {
				return true;
			}

			return props.allowedKeys.some((allowedKey: string) => translationKey === allowedKey || translationKey.startsWith(allowedKey + "."));
		};

		/** Translations grouped by translation key for the active language columns */
		const translationRows = computed<TranslationTableRow[]>(() => {
			const activeLanguageCodes = new Set(activeLanguageOptions.value.map((language: ActiveTranslationCountry) => language.countryCode));
			const rows = new Map<string, TranslationTableRow>();

			translations.value.forEach((translation: TranslationRow) => {
				if (!translation.translationKey || !translation.lang || !activeLanguageCodes.has(translation.lang) || !isKeyAllowed(translation.translationKey)) {
					return;
				}

				let row = rows.get(translation.translationKey);
				if (!row) {
					row = {
						translationKey: translation.translationKey,
						translations: {},
						hasStoredTranslations: false,
					};
					rows.set(translation.translationKey, row);
				}

				row.translations[translation.lang] = translation.translation ?? '';
				row.hasStoredTranslations = row.hasStoredTranslations || translation.id !== undefined && translation.id !== null;
			});

			return Array.from(rows.values());
		});

		/** Row focus/highlight driven by the "focusKey" route query (set by the DexInputText translation icon). */
		const rowFocus = useTableRowFocus<TranslationTableRow>({getRows: () => translationRows.value, rowKey: (row: TranslationTableRow) => row.translationKey});

		/** Keys that exist in the system but currently have no stored translation entries */
		const availableTranslationKeys = computed<{translationKey: string}[]>(() => {
			return translationRows.value
				.filter((row: TranslationTableRow) => !row.hasStoredTranslations)
				.map((row: TranslationTableRow) => ({translationKey: row.translationKey}));
		});

		/** Get translations from the API */
		const getTranslations = async () => {
			loadingTranslations.value = true;
			const filter = props.fixedFilter.trim() || undefined;

			try {
				translations.value = await ClientManager.getInstance().squeeze.translations.getTranslations(filter, true);

				// Expose all loaded keys
				emit("keysLoaded", Array.from(new Set(translations.value.map((row: TranslationRow) => row.translationKey).filter((key): key is string => !!key))));

				// Highlight the row requested via the "focusKey" query once the data is available.
				await rowFocus.focusRequestedRow();
			} catch (err) {
				ToastManager.showError(toast, t('Squeeze.General.Error'), err as Response);
			} finally {
				loadingTranslations.value = false;
			}
		}

		/**
		 * Returns a translation value for one language column.
		 * @param row
		 * @param languageCode
		 */
		const getTranslationValue = (row: TranslationTableRow, languageCode?: string) => {
			if (!languageCode) {
				return '';
			}
			return row.translations[languageCode] ?? '';
		}

		/** Resets the create/edit dialog model */
		const resetTranslationEntry = () => {
			translationEntry.translationKey = '';
			translationEntry.translations = {};
			activeLanguageOptions.value.forEach((language: ActiveTranslationCountry) => {
				translationEntry.translations[language.countryCode] = '';
			});
			showValidationMessage.value = false;
		}

		/**
		 * Opens the dialog to create translations for a not-yet-stored key.
		 * @param row
		 */
		const openCreateDialog = (row?: TranslationTableRow) => {
			isEditMode.value = false;
			resetTranslationEntry();
			if (row) {
				translationEntry.translationKey = row.translationKey;
			}
			showEditDialog.value = true;
		}

		/**
		 * Opens the dialog to update translations for an existing key.
		 * @param row
		 */
		const openEditDialog = (row: TranslationTableRow) => {
			if (!row.hasStoredTranslations) {
				return;
			}

			isEditMode.value = true;
			resetTranslationEntry();
			translationEntry.translationKey = row.translationKey;
			activeLanguageOptions.value.forEach((language: ActiveTranslationCountry) => {
				translationEntry.translations[language.countryCode] = getTranslationValue(row, language.countryCode);
			});
			showEditDialog.value = true;
		}

		/** Closes the create/edit dialog */
		const closeEditDialog = () => {
			showEditDialog.value = false;
			showValidationMessage.value = false;
		}

		/**
		 * Opens the delete confirmation dialog for an existing translation key.
		 * @param row
		 */
		const openDeleteDialog = (row: TranslationTableRow) => {
			selectedTranslation.value = row;
			deleteDialog.value = true;
		}

		/** Converts the dialog model into the API request body */
		const createTranslationPayload = () => {
			const translationTexts = activeLanguageOptions.value
				.map((language: ActiveTranslationCountry): TranslationText => {
					const value = translationEntry.translations[language.countryCode] ?? '';
					return {
						lang: language.countryCode,
						translation: value.trim() === '' ? null : value,
					};
				});

			return {
				translationKey: translationEntry.translationKey,
				translations: translationTexts,
			} as TranslationSet;
		}

		/** Creates or updates translations for all active languages in the dialog */
		const saveTranslation = async () => {
			if (isTranslationKeyInvalid.value) {
				showValidationMessage.value = true;
				return;
			}

			loadingDialog.value = true;
			try {
				const payload = createTranslationPayload();
				if (isEditMode.value) {
					await ClientManager.getInstance().squeeze.translations.putTranslation(payload);
				} else {
					await ClientManager.getInstance().squeeze.translations.postTranslation(payload);
				}

				ToastManager.showSuccess(toast, t('Squeeze.General.Success'), t('Squeeze.General.SaveSuccess'));
				closeEditDialog();
				await getTranslations();
			} catch (err) {
				ToastManager.showError(toast, t('Squeeze.General.Error'), err as Response);
			} finally {
				loadingDialog.value = false;
			}
		}

		/** Deletes all stored translations for the selected key */
		const deleteTranslation = async () => {
			if (!selectedTranslation.value) {
				return;
			}

			deleteDialog.value = false;
			loadingTranslations.value = true;
			try {
				await ClientManager.getInstance().squeeze.translations.deleteTranslation(selectedTranslation.value.translationKey);

				ToastManager.showSuccess(toast, t('Squeeze.General.Success'), t('Squeeze.General.SaveSuccess'));
				await getTranslations();
			} catch (err) {
				ToastManager.showError(toast, t('Squeeze.General.Error'), err as Response);
			} finally {
				selectedTranslation.value = null;
				loadingTranslations.value = false;
			}
		}

		initFilters();
		getTranslations();

		onMounted(() => {
			store.dispatch('fetchTranslationCountries')
				.catch((response: Response) => ToastManager.showError(toast, t('Squeeze.General.Error'), response));
		});

		watch(() => props.fixedFilter, () => {
			getTranslations();
		});

		watch(activeLanguageOptions, () => {
			initFilters();
		});

		return {
			...rowFocus,
			activeLanguageOptions,
			availableTranslationKeys,
			dialogHeaderText,
			filters,
			isEditMode,
			isTranslationKeyInvalid,
			loadingDialog,
			loadingTranslations,
			translationEntry,
			translationRows,
			deleteDialog,
			showEditDialog,
			showValidationMessage,
			closeEditDialog,
			deleteTranslation,
			getTranslationValue,
			openCreateDialog,
			openDeleteDialog,
			openEditDialog,
			saveTranslation,
		};
	},
});
</script>

<style scoped>

.child-content {
	display: flex;
	flex-direction: column;
	height: 100%;
	min-height: 0;
}

.translation-table {
	flex: 1 1 auto;
	min-height: 0;
}

::v-deep(.translation-table.p-datatable) {
	display: flex;
	flex-direction: column;
}

::v-deep(.translation-table .p-datatable-wrapper) {
	flex: 1 1 auto;
}

/*
 * Anpassung der Filter Inputfelder an den Stil des restlichen Systems
 */
</style>
